import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import tokens from '../theme/tokens';

// 입력창 — 한 줄에서 네 줄까지 늘어나는 textarea + 보내기. Enter 전송, Shift+Enter 줄바꿈
const MAX = 1000;
const LINE = 22;

const useStyles = makeStyles(() => ({
  root: {
    flex: 'none',
    padding: '10px 12px 12px',
    borderTop: `1px solid ${tokens.lineSoft}`,
    backgroundColor: tokens.surface,
  },
  error: {
    marginBottom: 6,
    fontSize: 12,
    color: tokens.primaryDark,
    letterSpacing: -0.4,
  },
  field: {
    display: 'flex',
    alignItems: 'flex-end',
    padding: '6px 6px 6px 14px',
    borderRadius: 22,
    border: `1px solid ${tokens.line}`,
    backgroundColor: tokens.surface,
    transition: 'border-color .12s, box-shadow .12s',
    '&:focus-within': { borderColor: tokens.primary, boxShadow: tokens.shadow },
  },
  textarea: {
    flex: '1 1 auto',
    minWidth: 0,
    maxHeight: LINE * 4,
    padding: '5px 0',
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontSize: 14,
    lineHeight: `${LINE}px`,
    fontFamily: 'inherit',
    letterSpacing: -0.4,
    color: tokens.ink,
    backgroundColor: 'transparent',
    '&::placeholder': { color: tokens.placeholder },
  },
  send: {
    flex: 'none',
    width: 32,
    height: 32,
    marginLeft: 6,
    borderRadius: 16,
    backgroundColor: tokens.primary,
    color: tokens.surface,
    transition: 'background-color .12s',
    '&:hover': { backgroundColor: tokens.primaryDark },
    '&$off': { backgroundColor: tokens.line, color: tokens.surface },
  },
  off: {},
  icon: { width: 16, height: 16 },
}));

function ChatComposer({ onSend, onTyping, sending = false, disabled = false, error = '', draft = '', onDraftUsed }) {
  const cs = useStyles();
  const [text, setText] = useState('');
  const ref = useRef(null);

  // 다른 화면이 넘긴 초안 — 입력창에 넣고 포커스. 고객이 고쳐서 보낸다
  useEffect(() => {
    if (!draft) return;
    setText(draft.slice(0, MAX));
    window.requestAnimationFrame(() => { resize(); if (ref.current) ref.current.focus(); });
    if (onDraftUsed) onDraftUsed();
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps
  const canSend = text.trim().length > 0 && !sending && !disabled;

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, LINE * 4 + 10)}px`;
  };

  const submit = async () => {
    if (!canSend) return;
    const ok = await onSend(text);
    if (ok) {
      setText('');
      if (onTyping) onTyping(true);
      window.requestAnimationFrame(resize);
    }
  };

  const onChange = (e) => {
    const v = e.target.value.slice(0, MAX);
    setText(v);
    resize();
    if (onTyping) onTyping(v.trim().length === 0);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className={cs.root}>
      {error && <div className={cs.error}>{error}</div>}
      <div className={cs.field}>
        <textarea
          ref={ref}
          className={cs.textarea}
          rows={1}
          value={text}
          placeholder="메시지를 입력하세요"
          onChange={onChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          aria-label="메시지 입력"
        />
        <ButtonBase className={clsx(cs.send, !canSend && cs.off)} onClick={submit} disabled={!canSend} aria-label="보내기" focusRipple={false}>
          <svg className={cs.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 12 20 4l-4 16-4.5-6.5L4 12z" />
            <path d="M11.5 13.5 20 4" />
          </svg>
        </ButtonBase>
      </div>
    </div>
  );
}

export default ChatComposer;
